import React, { useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '../../contexts/StoreContext';
import { Modal } from '../ui/Modal';
import { Field } from '../ui/Field';
import { OfferEditor } from './OfferEditor';
import { Brand } from '../../types/catalog';
import { formatDate, formatPct } from '../../utils/format';
const slugify = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
function BrandEditor({
  brand,
  onDone
}: {
  brand: Brand | null;
  onDone: () => void;
}) {
  const {
    brands,
    updateBrand,
    createBrand
  } = useStore();
  const [draft, setDraft] = useState<Brand>(brand ?? {
    id: '',
    name: '',
    slug: '',
    tagline: '',
    defaultOffer: {
      ...brands[0].defaultOffer,
      tna: null,
      verified: false,
      promo: null
    }
  });
  const [error, setError] = useState('');
  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim()) return setError('Ingresá el nombre de la marca.');
    if (draft.defaultOffer.terms.length === 0) return setError('Habilitá al menos un plazo.');
    if (brand) {
      updateBrand(brand.id, draft);
    } else {
      const slug = slugify(draft.name);
      if (brands.some(b => b.slug === slug)) return setError('Ya existe una marca con ese nombre.');
      createBrand({
        ...draft,
        id: slug,
        slug,
        name: draft.name.trim()
      });
    }
    toast.success('Marca guardada. Los cambios quedaron registrados.');
    onDone();
  };
  return <form onSubmit={save} className="space-y-5" noValidate data-id="element-74" data-mp="38kdopmkzwlug">
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-75" data-mp="2uojmtnin2wqu">
        <Field label="Nombre" htmlFor="br-name" data-id="element-76" data-mp="2zls65rfix6to">
          <input id="br-name" className="input" value={draft.name} onChange={e => setDraft({
          ...draft,
          name: e.target.value
        })} disabled={!!brand} maxLength={40} data-id="element-77" data-mp="2zmlgf2doyegk" />
        </Field>
        <Field label="Bajada" htmlFor="br-tag" data-id="element-78" data-mp="xtnkgxomb5re">
          <input id="br-tag" className="input" value={draft.tagline} onChange={e => setDraft({
          ...draft,
          tagline: e.target.value
        })} maxLength={100} data-id="element-79" data-mp="xsceqsbm3nxn" />
        </Field>
      </div>
      <h3 className="text-sm font-semibold" data-id="element-80" data-mp="rd58u686wnsy">Condiciones generales de la marca</h3>
      <p className="-mt-3 text-xs text-mist" data-id="element-81" data-mp="mg54bzqznt3y">Se aplican a todas las unidades de la marca que no tengan una condición propia.</p>
      <OfferEditor value={draft.defaultOffer} onChange={o => setDraft({
      ...draft,
      defaultOffer: o
    })} idPrefix="br-of" data-id="element-82" data-mp="hidd77y64b0t" />
      {error && <p className="text-sm text-danger" role="alert" data-id="element-83" data-mp="1a4eicfhynckj">{error}</p>}
      <div className="sticky bottom-0 -mx-5 -mb-5 flex justify-end gap-2 border-t border-line bg-surface p-4" data-id="element-84" data-mp="1a70y32f08ydu">
        <button type="button" onClick={onDone} className="btn btn-ghost" data-id="element-85" data-mp="10f4q8zn49twf">Cancelar</button>
        <button type="submit" className="btn btn-primary" data-id="element-86" data-mp="10daf2d1ndg5j">Guardar</button>
      </div>
    </form>;
}
export function BrandsPanel() {
  const {
    brands,
    vehicles
  } = useStore();
  const [editing, setEditing] = useState<Brand | null | 'new'>(null);
  return <div data-id="element-87" data-mp="20g8gpf31ow84">
      <div className="flex justify-end" data-id="element-88" data-mp="20ghywghulz39">
        <button onClick={() => setEditing('new')} className="btn btn-primary" data-id="element-89" data-mp="25e1jdy55383n"><PlusIcon className="h-4 w-4" aria-hidden data-id="element-90" data-mp="25e9x1s936hfw" /> Nueva marca</button>
      </div>
      <ul className="mt-5 divide-y divide-line rounded-2xl border border-line" data-id="element-91" data-mp="2abjmv90e0v49">
        {brands.map(b => <li key={b.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-92" data-mp="2fdirem7v5y2s">
            <div data-id="element-93" data-mp="2jbq7deui6vcx">
              <p className="font-semibold" data-id="element-94" data-mp="2ocw0vevzmmni">{b.name} <span className="text-xs font-normal text-mist" data-id="element-95" data-mp="2obuewfdfofr0">· {vehicles.filter(v => v.brandId === b.id).length} unidades</span></p>
              <p className="text-xs text-mist" data-id="element-96" data-mp="2t5p76773u31p">
                TNA {formatPct(b.defaultOffer.tna)} · plazos {b.defaultOffer.terms.join('/')} · anticipo mín. {b.defaultOffer.minDownPct}% · vigencia {formatDate(b.defaultOffer.validUntil)} ·{' '}
                <span className={b.defaultOffer.verified ? 'text-ok' : 'text-warn'} data-id="element-97" data-mp="372vjpk18j5ih">{b.defaultOffer.verified ? 'verificada' : 'sin verificar'}</span>
              </p>
            </div>
            <button onClick={() => setEditing(b)} className="btn btn-ghost h-9" data-id="element-98" data-mp="3o14f324aykf">Editar condiciones</button>
          </li>)}
      </ul>
      <Modal open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? 'Nueva marca' : 'Editar marca'} wide data-id="element-99" data-mp="hdw93otxiysc">
        {editing !== null && <BrandEditor brand={editing === 'new' ? null : editing} onDone={() => setEditing(null)} data-id="element-100" data-mp="3lt4deyv7fuq4" />}
      </Modal>
    </div>;
}